import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from "react-oidc-context"
import { toast } from 'react-toastify'
import cn from 'classnames'
import { useSwipeable } from 'react-swipeable'
// import UnlockWalletLoaderImage from 'assets/unlock-wallet-loader.svg'
import { ReactComponent as CircleShine } from 'assets/circle-shine.svg'
import { ReactComponent as CircleWallet } from 'assets/circle-wallet.svg'
import styles from './UnlockWallet.module.sass'

import {
  addWallet,
  setAllWallets,
  setShowAddWallet,
} from '../../redux/userReducer'
import { RootState } from 'redux/store'
import { checkWalletValidity } from 'services/portfolio'

function UnlockWallet({
  onUnlock,
}: {
  onUnlock?: (walletAddress: string) => void
}) {
  const dispatch = useDispatch()
  const auth = useAuth()
  const wallets = useSelector((state: RootState) => state.user.wallets)
  const stripeConfirmation = useSelector(
    (state: RootState) => state.user.stripeConfirmation
  )
  
  const [walletAddresses, setWalletAddresses] = useState<string[]>([])
  const [circleActive, setCircleActive] = useState<boolean>(false)
  const [walletInput, setWalletInput] = useState<string>('')
  const [editMode, setEditMode] = useState<boolean | number>(false)

  // Initialize wallets from storage after AWS auth
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const storedWallets = sessionStorage.getItem('wallets')
      
      if (storedWallets) {
        try {
          const parsedWallets = JSON.parse(storedWallets)
          setWalletAddresses(parsedWallets)
          dispatch(setAllWallets(parsedWallets))
        } catch (error) {
          console.error('Error parsing stored wallets')

          // Clear invalid storage
          sessionStorage.removeItem('wallets')

          setWalletAddresses([])
          dispatch(setAllWallets([]))
        }
      } else {
        // Ensure clean state when no stored wallets
        setWalletAddresses([])
        dispatch(setAllWallets([]))
      }
    }
  }, [auth.isAuthenticated, auth.user, dispatch])

  // Add validation handling
  const validateAndProcessWallet = async (walletToCheck: string) => {
    try {
      if (!auth.user?.access_token) {
        toast.error('User not authenticated');
        setCircleActive(false);
        return { isValid: false, resolvedAddress: null };
      }
  
      const response = await checkWalletValidity([walletToCheck], auth.user.access_token);
      
      // Get the resolved address from the response
      const resolvedAddress = Object.keys(response.data)[0];
      
      // Check if we got a valid result for either the input or resolved address
      if (!response.data[walletToCheck] && !response.data[resolvedAddress]) {
        toast.error(`Invalid wallet address: ${walletToCheck}`);
        setCircleActive(false);
        return { isValid: false, resolvedAddress: null };
      }
      
      return { isValid: true, resolvedAddress };
    } catch (error) {
      console.error('Wallet validation error');
      toast.error('Failed to validate wallet address');
      setCircleActive(false);
      return { isValid: false, resolvedAddress: null };
    }
  };

  const handlers = useSwipeable({
    onSwipedUp: (eventData) => {
      let editModeCopy = editMode
      if (editModeCopy !== false && typeof editModeCopy === 'number') {
        editModeCopy = editModeCopy + 1
        if (typeof editModeCopy === 'number') {
          setEditMode(editModeCopy)
          if (editModeCopy >= walletAddresses.length) {
            setEditMode(false)
            setWalletInput('')
          } else {
            setWalletInput(walletAddresses[editModeCopy])
          }
        }
      }
    },
    onSwipedDown: (eventData) => {
      let editModeCopy = editMode
      if (editModeCopy === false) {
        if (walletAddresses.length > 0) {
          setEditMode(walletAddresses.length - 1)
          setWalletInput(walletAddresses[walletAddresses.length - 1])
        }
      } else if (typeof editModeCopy === 'number') {
        editModeCopy = editModeCopy - 1
        if (editModeCopy >= 0) {
          setEditMode(editModeCopy)
          setWalletInput(walletAddresses[editModeCopy])
        }
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
  })

  const renderPreviousWallet = () => {
    if (walletAddresses.length > 0) {
      let indexToRender = walletAddresses.length - 1
      if (typeof editMode === 'number') {
        indexToRender = editMode - 1
      }
      if (indexToRender < 0) {
        return null
      }

      return (
        <div
          onClick={() => {
            setEditMode(indexToRender)
            setWalletInput(walletAddresses[indexToRender])
          }}
          className={cn(
            'input-primary',
            styles.nonFocusWallet,
            styles.previous
          )}
        >
          {walletAddresses[indexToRender]}
        </div>
      )
    }
    return null
  }

  const renderNextWallet = () => {
    if (editMode === false) {
      return null
    }

    if (walletAddresses.length > 0) {
      let indexToRender = 0
      if (typeof editMode === 'number') {
        indexToRender = editMode + 1
      }
      if (indexToRender >= walletAddresses.length) {
        return (
          <div
            onClick={() => {
              setEditMode(false)
              setWalletInput('')
            }}
            className={cn('input-primary', styles.nonFocusWallet, styles.next)}
          >
            Your Wallet Address
          </div>
        )
      }

      return (
        <div
          onClick={() => {
            setEditMode(indexToRender)
            setWalletInput(walletAddresses[indexToRender])
          }}
          className={cn('input-primary', styles.nonFocusWallet, styles.next)}
        >
          {walletAddresses[indexToRender]}
        </div>
      )
    }
    return null
  }


  const handleWalletSubmit = async () => {
    if (!auth.isAuthenticated) {
      toast.error('Please login first');
      return;
    }
  
    if (
      editMode === false &&
      walletAddresses?.length >= (stripeConfirmation?.max_wallets || 1)
    ) {
      toast.error('You reached the maximum limit of wallets');
      return;
    }
  
    setCircleActive(true);
    
    try {
      if (!auth.user?.access_token) {
        toast.error('Authentication token not found');
        setCircleActive(false);
        return;
      }
  
      // Validate and get resolved address in one step
      const { isValid, resolvedAddress } = await validateAndProcessWallet(walletInput);
      
      if (!isValid || !resolvedAddress) {
        setCircleActive(false);
        return;
      }
  
      // Process the valid wallet
      setTimeout(() => {
        if (editMode === false) {
          // Adding new wallet
          const newWallets = [...walletAddresses, resolvedAddress];
          setWalletAddresses(newWallets);
          dispatch(addWallet(resolvedAddress));
          setWalletInput('');
          if (onUnlock) {
            onUnlock(resolvedAddress);
          }
          sessionStorage.setItem('wallets', JSON.stringify(newWallets));
          toast.success('Wallet added successfully');
        } else {
          // Editing existing wallet
          const updatedWallets = walletAddresses.map((address, index) => 
            index === editMode ? resolvedAddress : address
          );
          
          dispatch(setAllWallets(updatedWallets));
          setWalletAddresses(updatedWallets);
          sessionStorage.setItem('wallets', JSON.stringify(updatedWallets));

          setWalletInput('');
          setEditMode(false);
          
          toast.success('Wallet updated successfully');
        }
        setCircleActive(false);
        dispatch(setShowAddWallet(false));
      }, 1000);
    } catch (error) {
      console.error('Error processing wallet');
      toast.error('Failed to process wallet address');
      setCircleActive(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.circleShine}>
        <CircleShine />
      </div>

      <div className={`${styles.circleWalletAdded} ${circleActive ? styles.circleActive : ''}`}>
        <CircleWallet />
      </div>
      
      <div {...handlers} className={styles.contentContainer}>
        <div className={styles.headerContainer}>
          <h1>Locked</h1>
          {/* <div>Enter EVM Wallet Address...</div> */}
        </div>
        
        <div className={styles.walletInputContainer}>
          <div className={styles.walletInput}>
            {renderPreviousWallet()}
            <input
              type="text"
              // placeholder="Your Wallet Address"
              placeholder="Enter EVM Wallet"
              value={walletInput}
              onChange={(e) => setWalletInput(e.target.value)}
              className={cn('input-primary', styles.focusWallet)}
            />
            {renderNextWallet()}
          </div>
        </div>

        <div
          className={cn('button-primary', {
            disabled: walletInput.length === 0,
          })}
          onClick={handleWalletSubmit}
        >
          {editMode === false ? 'Set New Wallet' : 'Update Wallet'}
        </div>
      </div>
    </div>
  )
}

export default UnlockWallet